/**
 * platformWiseColumnConfig
 * -------------------------
 * Stores table column configurations for each platform (e.g., amazon, myntra, flipkart).
 * Each platform maps to an array of column config objects.
 *
 * Column Config Properties:
 * - key: (string) Unique identifier for the column, usually matching a backend field.
 * - innerKeys: (object) Sub-mappings for rendering complex columns.
 *    - image: Field used for product image.
 *    - productName: Field used for product name.
 *    - asin: { label: string, value: string } → Display label + backend field for ASIN.
 * - title: (string) Tooltip title for the column.
 * - shortTitle: (string) Full display name for UI and exports.
 * - format: (string) Data type → "string", "number", "percent", "currency", etc.
 * - isSingleColumn: (boolean) If true, the column is merged into a single column in comparison downloads.
 * - dataIndex: (string) Backend mapping key (used for fetching data) — may be deprecated in future.
 * - isSticky: (boolean) If true, column stays fixed when scrolling horizontally.
 * - stickyLeft: (number) Left pixel offset for sticky placement.
 * - minWidth: (number) Minimum column width (in px).
 * - isDimension: (boolean) Marks dimension columns (non-metric fields like product name, category).
 * - viewType: (array) Determines visibility in different views: 
 *    ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"].
 *    Column will be shown if view matches any item in this array.
 * - alignment: (string) Text alignment inside the column → "left", "center", "right".
 * - colSpan: (number) Number of columns to span (for comparison header).
 *
 * Always update configs when:
 * - Adding or removing metrics/dimensions.
 * - Changing sticky column placement.
 * - Updating export/download requirements.
 * - Adding new view types.
 */

export const PerformanceAnalysisExtraColumnsConfig = {
    amazon: [
      {
        key: "placement_name",
        title: "Placement Name",
        shortTitle: "Placement",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "placement_name",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
      {
        key: "portfolio_name",
        title: "Portfolio Name",
        shortTitle: "Portfolio Name",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "portfolio_name",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
      {
        key: "campaign_type",
        title: "Campaign Type",
        shortTitle: "Campaign Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_type",
        minWidth: 140,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
      {
        key: "campaign_format",
        title: "Campaign Format",
        shortTitle: "Campaign Format",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_format",
        minWidth: 140,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
      {
        key: "targeting_type",
        title: "Targeting Type",
        shortTitle: "Targeting Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "targeting_type",
        minWidth: 140,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
    ],
    myntra: [
      {
        key: "campaign_targeting",
        title: "Campaign Targeting",
        shortTitle: "Campaign Targeting",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_targeting",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "campaign_type",
        title: "Campaign Type",
        shortTitle: "Campaign Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_type",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "placement_name",
        title: "Placement Name",
        shortTitle: "Placement Name",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "placement_name",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "date",
        title: "Date",
        shortTitle: "Date",
        type: "date",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "date",
        minWidth: 160,
        format: "date",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "week",
        title: "Week",
        shortTitle: "Week",
        type: "week",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "week",
        minWidth: 160,
        format: "week",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "month",
        title: "Month",
        shortTitle: "Month",
        type: "month",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "month",
        minWidth: 160,
        format: "month",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "ad_group_id",
        innerKeys: {
          image: "",
          productName: "ad_group_name",
          asin: {label:"Ad Group ID", value:"ad_group_id"},
  
        },
        title: "Ad Group",
        shortTitle: "Ad Group",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "ad_group_id",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "keyword",
        title: "Keyword",
        shortTitle: "Keyword",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "keyword",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "keyword_type",
        title: "Targeting Type",
        shortTitle: "Targeting Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "keyword_type",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "search_term",
        title: "Search Term",
        shortTitle: "Search Term",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "search_term",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      }
    ],
    flipkart: [
      {
        key: "campaign_targeting",
        title: "Campaign Targeting",
        shortTitle: "Campaign Targeting",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_targeting",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "campaign_type",
        title: "Campaign Type",
        shortTitle: "Campaign Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "campaign_type",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "placement_name",
        title: "Placement Name",
        shortTitle: "Placement Name",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "placement_name",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "date",
        title: "Date",
        shortTitle: "Date",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "date",
        minWidth: 160,
        format: "date",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "week",
        title: "Week",
        shortTitle: "Week",
        type: "week",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "week",
        minWidth: 160,
        format: "week",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "month",
        title: "Month",
        shortTitle: "Month",
        type: "month",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "month",
        minWidth: 160,
        format: "month",
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "ad_group_id",
        innerKeys: {
          image: "",
          productName: "ad_group_name",
          asin: {label:"Ad Group ID", value:"ad_group_id"},
  
        },
        title: "Ad Group",
        shortTitle: "Ad Group",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "ad_group_id",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "keyword",
        title: "Keyword",
        shortTitle: "Keyword",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "keyword",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "keyword_type",
        title: "Targeting Type",
        shortTitle: "Targeting Type",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "keyword_type",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
      {
        key: "search_term",
        title: "Search Term",
        shortTitle: "Search Term",
        type: "string",
        isSticky: true,
        isSingleColumn: true,
        dataIndex: "search_term",
        minWidth: 160,
        isDimension: true,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1
      },
    ],
    
  }
  
  export const PerformanceAnalysisColumnConfig = {
    amazon: [
      {
        key: "campaign_id",
        innerKeys: {
          image: "",
          productName: "campaign_name",
          asin: { label: "Campaign ID", value: "campaign_id" },
        },
        title: "Campaigns",
        shortTitle: "Campaign Name",
        type: "string",
        isSingleColumn: true,
        dataIndex: "campaign_id",
        isDimension: true,
        isSticky: true,
        stickyLeft: 0,
        minWidth: 240,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
      },
      ...PerformanceAnalysisExtraColumnsConfig["amazon"],
      {
        key: "bid_adjustment_value",
        title: "Bid Adjustment Value",
        shortTitle: "Bid Adjustment Value",
        type: "number",
        dataIndex: "bid_adjustment_value",
        minWidth: 160,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
        isSingleColumn: true,
      },
      {
        key: "campaign_bid_strategy",
        title: "Bid Strategy",
        shortTitle: "Bid Strategy",
        type: "string",
        dataIndex: "campaign_bid_strategy",
        minWidth: 120,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment: "left",
        colSpan: 1,
        isSingleColumn: true,
      },
      {
        key: "spends",
        type: "number",
        dataIndex: "spends",
        title: "Spends",
        shortTitle: "Spends",
        maximumFractionDigits: 0,
        isCurrencyFormat: true,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "revenue",
        type: "number",
        dataIndex: "revenue",
        title: "Revenue",
        shortTitle: "Revenue",
        maximumFractionDigits: 0,
        isCurrencyFormat: true,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "orders",
        type: "number",
        dataIndex: "orders",
        title: "Orders",
        shortTitle: "Orders",
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "roas",
        type: "number",
        dataIndex: "roas",
        title: "ROAS",
        shortTitle: "ROAS",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "ctr",
        type: "number",
        format: "percent",
        dataIndex: "ctr",
        title: "CTR",
        shortTitle: "CTR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "cpc",
        type: "number",
        dataIndex: "cpc",
        title: "CPC",
        shortTitle: "CPC",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "campaign_burn_rate",
        title: "DBR",
        shortTitle: "DBR",
        type: "number",
        dataIndex: "campaign_burn_rate",
        minWidth: 100,
        viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        colSpan: 1,
        isSingleColumn: true,
      },
      {
        key: "acos",
        type: "number",
        format: "percent",
        dataIndex: "acos",
        title: "ACOS",
        shortTitle: "ACOS",
        maximumFractionDigits: 2,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
    ],
    myntra: [
      {
        key: "campaign_id",
         innerKeys: {
          image: "",
          productName: "campaign_name",
          asin: {label:"Campaign ID", value:"campaign_id"},
  
        },
        title: "Campaigns",
        shortTitle: "Campaigns",
        type: "string",
        // isSingleColumn is used to show the column in single column in comparison download
        isSingleColumn: true,
        dataIndex: "campaign_id",
        isDimension: true,
        isSticky: true,
        stickyLeft: 0,
        minWidth: 240,
        viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment:"left",
        colSpan: 1,
        isDimension: true,
      },
      ...PerformanceAnalysisExtraColumnsConfig["flipkart"],

      {
        key: "spends",
        type: "number",
        dataIndex: "spends",
        title: "Spends",
        shortTitle: "Spends",
        maximumFractionDigits: 0,
        isCurrencyFormat: true,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "impressions",
        type: "number",
        dataIndex: "impressions",
        title: "Impressions",
        shortTitle: "Impressions",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        colSpan: 2,
      },
      {
        key: "cpm",
        type: "number",
        dataIndex: "cpm",
        title: "CPM",
        shortTitle: "CPM",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "clicks",
        type: "number",
        dataIndex: "clicks",
        title: "Clicks",
        shortTitle: "Clicks",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        colSpan: 2,
      },
       {
        key: "ctr",
        type: "number",
        format: "percent",
        dataIndex: "ctr",
        title: "CTR",
        shortTitle: "CTR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "cpc",
        type: "number",
        dataIndex: "cpc",
        title: "CPC",
        shortTitle: "CPC",
        viewType: ["AGGREGATE", "PERFORMANCE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "cvr", 
        type: "number",
        format: "percent",
        dataIndex: "cvr",
        title: "Ads CVR",
        shortTitle: "Ads CVR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "total_cvr", 
        type: "number",
        format: "percent",
        dataIndex: "total_cvr",
        title: "Total CVR",
        shortTitle: "Total CVR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "units_sold_direct",
        type: "number",
        dataIndex: "units_sold_direct",
        title: "Units (Direct)",
        shortTitle: "Units (Direct)",
        viewType: ["AGGREGATE", "ORDER"],
        colSpan: 2,
      },
      {
        key: "units_sold_indirect",
        type: "number",
        dataIndex: "units_sold_indirect",
        title: "Units (Indirect)",
        shortTitle: "Units (Indirect)",
        viewType: ["AGGREGATE", "ORDER"],
        colSpan: 2,
      },
      {
        key: "orders",
        type: "number",
        dataIndex: "orders",
        title: "Ads Units",
        shortTitle: "Ads Units",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "order_units",
        type: "number",
        dataIndex: "order_units",
        title: "Total Units",
        shortTitle: "Total Units",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "revenue_direct",
        type: "number",
        dataIndex: "revenue_direct",
        title: "Revenue (Direct)",
        shortTitle: "Revenue (Direct)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue_indirect",
        type: "number",
        dataIndex: "revenue_indirect",
        title: "Revenue (Indirect)",
        shortTitle: "Revenue (Indirect)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue",
        type: "number",
        dataIndex: "revenue",
        title: "Ads Revenue",
        shortTitle: "Ads Revenue",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "gmv",
        type: "number",
        dataIndex: "gmv",
        title: "Total Revenue (GMV)",
        shortTitle: "Total Revenue (GMV)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "direct_revenue_percentage",
        type: "number",
        format: "percent",
        dataIndex: "direct_revenue_percentage",
        title: "Revenue Direct (Percentage)",
        shortTitle: "Revenue Direct (Percentage)",
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "indirect_revenue_percentage",
        type: "number",
        format: "percent",
        dataIndex: "indirect_revenue_percentage",
        title: "Revenue Indirect (Percentage)",
        maximumFractionDigits: 0,
        shortTitle: "Revenue Indirect (Percentage)",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "direct_roas",
        type: "number",
        dataIndex: "direct_roas",
        title: "ROAS Direct",
        shortTitle: "ROAS Direct",
        viewType: ["AGGREGATE"],
        maximumFractionDigits: 1,
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "roas",
        type: "number",
        dataIndex: "roas",
        title: "Ads ROAS",
        shortTitle: "Ads ROAS",
        maximumFractionDigits: 1,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "total_roas",
        type: "number",
        dataIndex: "total_roas",
        title: "Total ROAS",
        shortTitle: "Total ROAS",
        maximumFractionDigits: 1,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "aov",
        type: "number",
        dataIndex: "aov",
        title: "Average Order Value",
        shortTitle: "Average Order Value",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "acos",
        type: "number",
        dataIndex: "acos",
        title: "ACOS",
        format: "percent",
        shortTitle: "ACOS",
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "spends_drr",
        type: "number",
        dataIndex: "spends_drr",
        title: "Spends (DRR)",
        shortTitle: "Spends (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "clicks_drr",
        type: "number",
        dataIndex: "clicks_drr",
        title: "Clicks (DRR)",
        shortTitle: "Clicks (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "ctr_drr",
        type: "number",
        format: "percent",
        dataIndex: "ctr_drr",
        title: "CTR (DRR)",
        shortTitle: "CTR (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "cpc_drr",
        type: "number",
        dataIndex: "cpc_drr",
        title: "CPC (DRR)",
        shortTitle: "CPC (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "cvr_drr",
        type: "number",
        format: "percent",
        dataIndex: "cvr_drr",
        title: "CVR (DRR)",
        shortTitle: "CVR (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
     
      {
        key: "units_sold_drr",
        type: "number",
        dataIndex: "units_sold_drr",
        title: "Units (DRR)",
        shortTitle: "Units (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "units_sold_direct_drr",
        type: "number",
        dataIndex: "units_sold_direct_drr",
        title: "Units (Direct) (DRR)",
        maximumFractionDigits: 0,
        shortTitle: "Units (Direct) (DRR)",
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "units_sold_indirect_drr",
        type: "number",
        dataIndex: "units_sold_indirect_drr",
        title: "Units (Indirect) (DRR)",
        shortTitle: "Units (Indirect) (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "revenue_direct_drr",
        type: "number",
        dataIndex: "revenue_direct_drr",
        title: "Revenue (Direct) (DRR)",
        shortTitle: "Revenue (Direct) (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue_indirect_drr",
        type: "number",
        dataIndex: "revenue_indirect_drr",
        title: "Revenue (Indirect) (DRR)",
        shortTitle: "Revenue (Indirect) (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "revenue_drr",
        type: "number",
        dataIndex: "revenue_drr",
        title: "Revenue (DRR)",
        shortTitle: "Revenue (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "roas_drr",
        type: "number",
        dataIndex: "roas_drr",
        title: "ROAS (DRR)",
        shortTitle: "ROAS (DRR)",
        viewType: ["DRR"],
        maximumFractionDigits: 1,
        isDerivedValue: true,
        colSpan: 2,
      }
    ],
    flipkart: [
      {
        key: "campaign_id",
         innerKeys: {
          image: "",
          productName: "campaign_name",
          asin: {label:"Campaign ID", value:"campaign_id"},
  
        },
        title: "Campaigns",
        shortTitle: "Campaigns",
        type: "string",
        // isSingleColumn is used to show the column in single column in comparison download
        isSingleColumn: true,
        dataIndex: "campaign_id",
        isDimension: true,
        isSticky: true,
        stickyLeft: 0,
        minWidth: 240,
        viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
        alignment:"left",
        colSpan: 1,
        isDimension: true,
      },
      ...PerformanceAnalysisExtraColumnsConfig["flipkart"],

      {
        key: "spends",
        type: "number",
        dataIndex: "spends",
        title: "Spends",
        shortTitle: "Spends",
        maximumFractionDigits: 0,
        isCurrencyFormat: true,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "impressions",
        type: "number",
        dataIndex: "impressions",
        title: "Impressions",
        shortTitle: "Impressions",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        colSpan: 2,
      },
      {
        key: "cpm",
        type: "number",
        dataIndex: "cpm",
        title: "CPM",
        shortTitle: "CPM",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "clicks",
        type: "number",
        dataIndex: "clicks",
        title: "Clicks",
        shortTitle: "Clicks",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        colSpan: 2,
      },
       {
        key: "ctr",
        type: "number",
        format: "percent",
        dataIndex: "ctr",
        title: "CTR",
        shortTitle: "CTR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "cpc",
        type: "number",
        dataIndex: "cpc",
        title: "CPC",
        shortTitle: "CPC",
        viewType: ["AGGREGATE", "PERFORMANCE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "cvr", 
        type: "number",
        format: "percent",
        dataIndex: "cvr",
        title: "Ads CVR",
        shortTitle: "Ads CVR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "total_cvr", 
        type: "number",
        format: "percent",
        dataIndex: "total_cvr",
        title: "Total CVR",
        shortTitle: "Total CVR",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "units_sold_direct",
        type: "number",
        dataIndex: "units_sold_direct",
        title: "Units (Direct)",
        shortTitle: "Units (Direct)",
        viewType: ["AGGREGATE", "ORDER"],
        colSpan: 2,
      },
      {
        key: "units_sold_indirect",
        type: "number",
        dataIndex: "units_sold_indirect",
        title: "Units (Indirect)",
        shortTitle: "Units (Indirect)",
        viewType: ["AGGREGATE", "ORDER"],
        colSpan: 2,
      },
      {
        key: "orders",
        type: "number",
        dataIndex: "orders",
        title: "Ads Units",
        shortTitle: "Ads Units",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "order_units",
        type: "number",
        dataIndex: "order_units",
        title: "Total Units",
        shortTitle: "Total Units",
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "revenue_direct",
        type: "number",
        dataIndex: "revenue_direct",
        title: "Revenue (Direct)",
        shortTitle: "Revenue (Direct)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue_indirect",
        type: "number",
        dataIndex: "revenue_indirect",
        title: "Revenue (Indirect)",
        shortTitle: "Revenue (Indirect)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue",
        type: "number",
        dataIndex: "revenue",
        title: "Ads Revenue",
        shortTitle: "Ads Revenue",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "gmv",
        type: "number",
        dataIndex: "gmv",
        title: "Total Revenue (GMV)",
        shortTitle: "Total Revenue (GMV)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        colSpan: 2,
      },
      {
        key: "direct_revenue_percentage",
        type: "number",
        format: "percent",
        dataIndex: "direct_revenue_percentage",
        title: "Revenue Direct (Percentage)",
        shortTitle: "Revenue Direct (Percentage)",
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "indirect_revenue_percentage",
        type: "number",
        format: "percent",
        dataIndex: "indirect_revenue_percentage",
        title: "Revenue Indirect (Percentage)",
        maximumFractionDigits: 0,
        shortTitle: "Revenue Indirect (Percentage)",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "direct_roas",
        type: "number",
        dataIndex: "direct_roas",
        title: "ROAS Direct",
        shortTitle: "ROAS Direct",
        viewType: ["AGGREGATE"],
        maximumFractionDigits: 1,
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "roas",
        type: "number",
        dataIndex: "roas",
        title: "Ads ROAS",
        shortTitle: "Ads ROAS",
        maximumFractionDigits: 1,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "total_roas",
        type: "number",
        dataIndex: "total_roas",
        title: "Total ROAS",
        shortTitle: "Total ROAS",
        maximumFractionDigits: 1,
        viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "aov",
        type: "number",
        dataIndex: "aov",
        title: "Average Order Value",
        shortTitle: "Average Order Value",
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "acos",
        type: "number",
        dataIndex: "acos",
        title: "ACOS",
        format: "percent",
        shortTitle: "ACOS",
        maximumFractionDigits: 0,
        viewType: ["AGGREGATE"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "spends_drr",
        type: "number",
        dataIndex: "spends_drr",
        title: "Spends (DRR)",
        shortTitle: "Spends (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "clicks_drr",
        type: "number",
        dataIndex: "clicks_drr",
        title: "Clicks (DRR)",
        shortTitle: "Clicks (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "ctr_drr",
        type: "number",
        format: "percent",
        dataIndex: "ctr_drr",
        title: "CTR (DRR)",
        shortTitle: "CTR (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "cpc_drr",
        type: "number",
        dataIndex: "cpc_drr",
        title: "CPC (DRR)",
        shortTitle: "CPC (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "cvr_drr",
        type: "number",
        format: "percent",
        dataIndex: "cvr_drr",
        title: "CVR (DRR)",
        shortTitle: "CVR (DRR)",
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
     
      {
        key: "units_sold_drr",
        type: "number",
        dataIndex: "units_sold_drr",
        title: "Units (DRR)",
        shortTitle: "Units (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "units_sold_direct_drr",
        type: "number",
        dataIndex: "units_sold_direct_drr",
        title: "Units (Direct) (DRR)",
        maximumFractionDigits: 0,
        shortTitle: "Units (Direct) (DRR)",
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "units_sold_indirect_drr",
        type: "number",
        dataIndex: "units_sold_indirect_drr",
        title: "Units (Indirect) (DRR)",
        shortTitle: "Units (Indirect) (DRR)",
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "revenue_direct_drr",
        type: "number",
        dataIndex: "revenue_direct_drr",
        title: "Revenue (Direct) (DRR)",
        shortTitle: "Revenue (Direct) (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
      {
        key: "revenue_indirect_drr",
        type: "number",
        dataIndex: "revenue_indirect_drr",
        title: "Revenue (Indirect) (DRR)",
        shortTitle: "Revenue (Indirect) (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        isDerivedValue: true,
        colSpan: 2,
      },
       {
        key: "revenue_drr",
        type: "number",
        dataIndex: "revenue_drr",
        title: "Revenue (DRR)",
        shortTitle: "Revenue (DRR)",
        isCurrencyFormat: true,
        maximumFractionDigits: 0,
        viewType: ["DRR"],
        colSpan: 2,
      },
      {
        key: "roas_drr",
        type: "number",
        dataIndex: "roas_drr",
        title: "ROAS (DRR)",
        shortTitle: "ROAS (DRR)",
        viewType: ["DRR"],
        maximumFractionDigits: 1,
        isDerivedValue: true,
        colSpan: 2,
      }
    ],
  };
  
  
  export const getPerformanceColumnsConfig = (
    visibleColumns,
    isComparisonApi = false
  ) => {
    const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
    let columnConfig = PerformanceAnalysisColumnConfig[userPlatform];
    //first map then filter because we need to replace the tableName first and then filter the visible columns
    return columnConfig
      .map((column) => {
        return {
          ...column,
          key: column.key,
          dataIndex: `${
            !column?.isSingleColumn && isComparisonApi ? "startDate-endDate," : ""
          }${
            column.dataIndex
          }`,
        };
      })
      .filter((column) => {
        return visibleColumns.includes(column.key);
      });
  };
  
  export const getPerformanceAllColumns = (isComparisonApi = false, dimensions = []) => {
    const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
    let columnConfig = PerformanceAnalysisColumnConfig[userPlatform];
  
    const dimensionOrderMap = new Map(
      (dimensions || []).map((dimension, index) => [dimension, index])
    );

    const updatedAndSortedAndFilteredColumnConfig = [...columnConfig]
      .sort((a, b) => {
        const aOrder = dimensionOrderMap.has(a.key)
          ? dimensionOrderMap.get(a.key)
          : Number.MAX_SAFE_INTEGER;
        const bOrder = dimensionOrderMap.has(b.key)
          ? dimensionOrderMap.get(b.key)
          : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return 0;
      })
      .filter((column) => {
        if (!dimensions.includes(column.key) && column.isDimension) {
          return false;
        }
        return true;
      });
  
    let cumulativeStickyLeft = 0;
  
    return updatedAndSortedAndFilteredColumnConfig.map((column, index) => {
      const minWidth = column.minWidth || 120;
  
      const updatedColumn = {
        ...column,
        stickyLeft: index === 0 ? 0 : cumulativeStickyLeft,
        key: column.key,
        dataIndex: `${
          !column?.isSingleColumn && isComparisonApi
            ? "startDate-endDate,"
            : ""
        }${column.dataIndex}`,
      };
  
      // increment cumulative width AFTER assigning stickyLeft
      cumulativeStickyLeft += minWidth;
  
      return updatedColumn;
    });
  }
  
  const dashboardColumns = [
    "spends",
    "clicks",
    "ctr",
    "cpc",
    "cvr",
    "total_cvr",
    "orders",
    "order_units",
    "revenue",
    "gmv",
    "roas",
    "total_roas"
  ];
  
  export const getDashboardColumnsWithoutDimensions = () => {
    const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
    const columnConfig = PerformanceAnalysisColumnConfig[userPlatform];
    return columnConfig?.filter((column) => {
      return dashboardColumns.includes(column.key) && !column.isDimension;
    }) ?? [];
  }
  
  // for home dashboard new
  export const getAllColumnsWithoutDimensions = () => {
    const userPlatform = JSON.parse(localStorage.getItem("user_platform")); 
    const columnConfig = PerformanceAnalysisColumnConfig[userPlatform];
    return columnConfig?.filter((column) => {
      return !column.isDimension;
    }) ?? [];
  };

  export const PerformanceAnalysisMeasures = {
    amazon: ["spends", "revenue", "orders", "roas", "ctr", "cpc", "acos"],
    myntra: [
      "clicks",
            "spends",
            "ctr",
            "cvr",
            "revenue",
            "units_sold_direct",
            "units_sold_indirect",
            "revenue_direct",
            "revenue_indirect",
            "roas",
            "cpc",
            "impressions",
            "orders",
            "cpm",
            "aov",
            "acos",
            "clicks_drr",
            "spends_drr",
            "ctr_drr",
            "cvr_drr",
            "units_sold_drr",
            "units_sold_direct_drr",
            "units_sold_indirect_drr",
            "revenue_direct_drr",
            "revenue_indirect_drr",
            "roas_drr",
            "cpc_drr",
            "revenue_drr",
            "direct_roas",
            "direct_revenue_percentage",
            "indirect_revenue_percentage",
    ],
    flipkart: [
      "clicks",
            "spends",
            "ctr",
            "cvr",
            "revenue",
            "units_sold_direct",
            "units_sold_indirect",
            "revenue_direct",
            "revenue_indirect",
            "roas",
            "cpc",
            "impressions",
            "orders",
            "cpm",
            "aov",
            "acos",
            "clicks_drr",
            "spends_drr",
            "ctr_drr",
            "cvr_drr",
            "units_sold_drr",
            "units_sold_direct_drr",
            "units_sold_indirect_drr",
            "revenue_direct_drr",
            "revenue_indirect_drr",
            "roas_drr",
            "cpc_drr",
            "revenue_drr",
            "direct_roas",
            "direct_revenue_percentage",
            "indirect_revenue_percentage",
    ],
  };
  
  export const PerformanceAnalysisDefaultDimension = {
    amazon: ["campaign_id", "campaign_name", "placement_name"],
    myntra: ["campaign_id", "campaign_name"],
    flipkart: ["campaign_id", "campaign_name"],
  };

  /** Dimension levels that behave like date: single dimension, account-level API, DATE_MEASURES, no comparison view */
  export const TIME_LEVEL_DIMENSIONS = ["date", "week", "month"];

  export const PerformanceAnalysisGroupByOptions = {
    amazon: [
      {
        label: "Campaign + Placement",
        value: "campaign_placement",
        dimensions: ["campaign_id", "campaign_name", "placement_name"],
      },
    ],
    myntra: [
      { label: "Date", value: "date", dimensions: ["date"], disabledDimensions: ["keyword", "search_term", "week", "month"] },
      { label: "Week", value: "week", dimensions: ["week"], disabledDimensions: ["keyword", "search_term", "date", "month"] },
      { label: "Month", value: "month", dimensions: ["month"], disabledDimensions: ["keyword", "search_term", "date", "week"] },
      { label: "Campaign Name", value: "campaign_id", dimensions: ["campaign_id", "campaign_name"]},
      { label: "Campaign Targeting", value: "campaign_targeting", dimensions: ["campaign_targeting"]},
      { label: "Campaign Type", value: "campaign_type", dimensions: ["campaign_type"]},
      { label: "Placement Name", value: "placement_name", dimensions: ["placement_name"],disabledDimensions: ["keyword", "search_term"] },
      { label: "Ad Group", value: "ad_group_id", dimensions: ["ad_group_id","ad_group_name"],disabledDimensions: ["keyword", "search_term"] },
      { label: "Keyword", value: "keyword", dimensions: ["keyword"],disabledDimensions: ["date", "week", "month", "placement_name", "ad_group_id", "ad_group_name", "search_term"] },
      { label: "Targeting Type", value: "keyword_type", dimensions: ["keyword_type"],disabledDimensions: ["placement_name", "ad_group_id", "ad_group_name"] },
      { label: "Search Term", value: "search_term", dimensions: ["search_term"],disabledDimensions: ["date", "week", "month", "placement_name", "ad_group_id", "ad_group_name", "keyword"] },
    ],
    flipkart: [
      { label: "Date", value: "date", dimensions: ["date"], disabledDimensions: ["keyword", "search_term", "week", "month"] },
      { label: "Week", value: "week", dimensions: ["week"], disabledDimensions: ["keyword", "search_term", "date", "month"] },
      { label: "Month", value: "month", dimensions: ["month"], disabledDimensions: ["keyword", "search_term", "date", "week"] },
      { label: "Campaign Name", value: "campaign_id", dimensions: ["campaign_id", "campaign_name"]},
      { label: "Campaign Targeting", value: "campaign_targeting", dimensions: ["campaign_targeting"]},
      { label: "Campaign Type", value: "campaign_type", dimensions: ["campaign_type"]},
      { label: "Placement Name", value: "placement_name", dimensions: ["placement_name"],disabledDimensions: ["keyword", "search_term"] },
      { label: "Ad Group", value: "ad_group_id", dimensions: ["ad_group_id","ad_group_name"],disabledDimensions: ["keyword", "search_term"] },
      { label: "Keyword", value: "keyword", dimensions: ["keyword"],disabledDimensions: ["date", "week", "month", "placement_name", "ad_group_id", "ad_group_name", "search_term"] },
      { label: "Targeting Type", value: "keyword_type", dimensions: ["keyword_type"],disabledDimensions: [ "placement_name", "ad_group_id", "ad_group_name"] },
      { label: "Search Term", value: "search_term", dimensions: ["search_term"],disabledDimensions: ["date", "week", "month", "placement_name", "ad_group_id", "ad_group_name", "keyword"] },
    ]
  };